import snakecaseKeys from 'snakecase-keys';
import { restClient } from '../../client';
import { PaymentWaiverReasonEnum } from '@uplift/types';
import { PaymentTypeName } from '../../resources';
import { PatientServiceItemStatus } from '../patientSessionHistory';
import { ChangePaymentTypeReason } from '../session';

export const enum ClaimStatusEnum {
  readyToSubmit = 'Ready to Submit',
  submitted = 'Submitted',
  onHold = 'On Hold',
  other = 'Other',
}

export const enum PaymentStateEnum {
  initial = 'initial',
  inProgress = 'in_progress',
  completed = 'completed',
  error = 'error',
  refunded = 'refunded',
  canceled = 'canceled'
}

export type SessionPaymentType = {
  name: string;
};

export enum ServiceItemPaymentStatus {
  unknown = 'unknown',
  outstanding = 'outstanding',
  processing = 'processing',
  completed = 'completed',
  refunded = 'refunded',
  canceled = 'canceled',
}

export type ClaimBalance = {
  chargeAmountCents: number;
  amountInsurancePaidCents: number;
  amountPatientPaidCents: number;
  updatedAt: string;
  createdAt: string;
  claimStatus: ClaimStatusEnum;
  totalAdjustmentCents: number;
  totalInsuranceBalanceCents: number;
  totalPatientBalanceCents: number;
  totalAllowedAmountCents: number;
};

export type Payment = {
  amountCents: number;
  resourceId: string;
  resourceType: 'appointment' | 'medical_note';
  state: PaymentStateEnum;
  type: PaymentTypeName;
  paymentWaiverReason: PaymentWaiverReasonEnum | null;
  waivedBy: string;
  refundedAt: string | null;
  createdAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;

  paymentTypeChangeReason?: ChangePaymentTypeReason;
  paymentTypeChangedBy?: string;
  paymentTypeChangedAt?: string;
};

export const enum SessionHistoryStatusEnum {
  NOT_AVAILABLE = 'N/A',
  PROCESSING = 'Processing',
  UNKNOWN = 'Unknown',
  UNSUBMITTED = 'Unsubmitted'
}

export type SessionHistoryMetaType = {
  lateFee: boolean | null;
  appointmentState: AppointmentStateEnum | null;
  canceledAt: string | null;
  cancelReason: string | null;
  canceledByProvider: string | null;
  waivedByProvider: string | null;
};

export const enum ResourceTypeEnum {
  APPOINTMENT = 'appointment',
  MEDICAL_NOTE = 'medical_note'
}

export const enum AppointmentStateEnum {
  APPOINTMENT_SUBMITTED = 'submited',
  APPOINTMENT_COMPLETED = 'completed',
  APPOINTMENT_CANCELLED = 'cancelled',
}

export type ServicePayment = {
  claim?: ClaimBalance;
  payment: Payment;
  insuranceName?: string | null;
  secondaryInsuranceName?: string | null;
  status: ServiceItemPaymentStatus;
  sponsoredBy?: string | null;
};

export type SessionHistoryServiceType = {
  lateFee?: boolean | null;
  appointmentState?: AppointmentStateEnum | null;
  canceledAt?: string | null;
  cancelReason?: string | null;
  canceledByProvider?: string | null;
  primaryCptCode?: string | null;
  cptCodes?: string[];
};

export type AdminSessionHistoryItem = {
  resourceType: ResourceTypeEnum;
  resourceId: string;
  serviceDate: string;
  status: PatientServiceItemStatus;
  provider: {
    id: string;
    name: string;
  };
  payment: ServicePayment | null;
  submittedAt: string | null;
  service: SessionHistoryServiceType;
  paymentRefund?: number | null;
  isBulkCharge?: boolean | null;
};

export type AdminSessionHistoryProps = {
  data: AdminSessionHistoryItem[];
};

export const getAdminSessionHistory = async (userId: string) =>
  restClient.request<AdminSessionHistoryProps>(`/api/v1/admin/patient/${userId}/session_history`, {
    method: 'GET',
  });

export type SkipSessionChargeType = {
  paymentWaiverReason: PaymentWaiverReasonEnum;
  resourceId: string;
  resourceType: ResourceTypeEnum;
};

export const skipSessionCharge = async (data: SkipSessionChargeType) =>
  restClient.request<SkipSessionChargeType>('/api/v1/admin/skip_payment', {
    method: 'POST',
    data: snakecaseKeys(data)
  });

export type RefundLateFeeType = {
  appointmentId: string;
  waivedBy: string;
  waiverReason: string;
};

export const refundLateFee = async ( data: RefundLateFeeType) =>
  restClient.request<RefundLateFeeType>('/api/v1/admin/claim_administration/refund_late_fee', {
    method: 'POST',
    data: snakecaseKeys(data)
  });
